import { Link, usePage } from '@inertiajs/react';
import { ActionIcon, Avatar, Divider, Group, Indicator, Stack, Tooltip } from '@mantine/core';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import KaliOutline from '@/components/assistant/KaliOutline';
import OrganizationAvatar from '@/components/organizations/OrganizationAvatar';
import { ASSISTANT_PATH } from '@/helpers/assistant';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useOrganizationsIndex } from '@/queries/internal_api/pro/organizations';
import { useUnresolvedConversationCountsIndex } from '@/queries/internal_api/pro/unresolved_conversation_counts';
import type { MinimalOrganization, SharedProps } from '@/types';

const TRANSPARENT_BORDER = '1px solid transparent';
const DESKTOP_BUTTON_SIZE = 48;
const DESKTOP_AVATAR_SIZE = 40;
const MOBILE_BUTTON_SIZE = 40;
const MOBILE_AVATAR_SIZE = 32;

export default function OrganizationsSidebar({
  getOrganizationHref,
  selectedOrganizationId,
  showUnresolvedCounts = false,
  disableExternalOrganizations = false,
}: {
  // Patients and conversations use an organization_id query parameter; settings use the ID in the path.
  // Each parent supplies its URL format so the sidebar stays independent of page routes.
  getOrganizationHref: (organizationId: string) => string;
  selectedOrganizationId?: string;
  showUnresolvedCounts?: boolean;
  // Pages that need an authorization, such as the stats, keep the external organizations in place
  // but out of reach, so the sidebar stays identical from one page to the next.
  disableExternalOrganizations?: boolean;
}) {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const { current_account: account } = usePage<SharedProps>().props;
  const assistantAvailable = Boolean(account?.is_super_admin);
  const { data: organizationGroups } = useOrganizationsIndex();
  const myOrganizations = organizationGroups?.my_organizations ?? [];
  const externalOrganizations = organizationGroups?.external_organizations ?? [];
  const { data } = useUnresolvedConversationCountsIndex({
    enabled: showUnresolvedCounts,
  });

  if (!assistantAvailable && myOrganizations.length + externalOrganizations.length < 2) return null;

  const assistantSelected = window.location.pathname === ASSISTANT_PATH;
  const avatarSize = isMobile ? MOBILE_AVATAR_SIZE : DESKTOP_AVATAR_SIZE;
  const sidebarLinkProps = (selected: boolean) => ({
    'aria-current': selected ? ('page' as const) : undefined,
    size: isMobile ? MOBILE_BUTTON_SIZE : DESKTOP_BUTTON_SIZE,
    radius: 'md',
    variant: selected ? 'light' : 'subtle',
    color: selected ? 'brand' : 'gray',
    bd: selected ? '1px solid var(--mantine-primary-color-light-color)' : TRANSPARENT_BORDER,
  });

  const renderOrganizations = (organizations: MinimalOrganization[], isExternal = false) =>
    organizations.map((organization) => {
      // A disabled query still serves whatever the inbox already cached, so the flag has to gate
      // the read and not only the request.
      const unresolvedCount = showUnresolvedCounts ? data?.counts[organization.id] : undefined;
      const isSelected = organization.id === selectedOrganizationId;
      const isDisabled = isExternal && disableExternalOrganizations;
      const tooltipLabel = isExternal
        ? t(isDisabled ? 'organizations.external_unavailable' : 'conversations.external_organization', {
            organization: organization.name,
          })
        : organization.name;
      // `data-disabled` rather than `disabled`, which would swallow the pointer events the
      // tooltip needs to explain why the organization is out of reach.
      const disabledProps = isDisabled
        ? { 'data-disabled': true, 'aria-disabled': true, onClick: (event: MouseEvent) => event.preventDefault() }
        : {};

      return (
        <Tooltip
          key={organization.id}
          label={tooltipLabel}
          position={isMobile ? 'bottom' : 'right'}
          offset={12}
          withArrow
        >
          <Indicator
            inline
            disabled={!unresolvedCount}
            label={unresolvedCount}
            size={18}
            offset={4}
            withBorder
            aria-label={unresolvedCount ? t('organizations.unresolved_count') : undefined}
          >
            <ActionIcon
              component={Link}
              href={getOrganizationHref(organization.id)}
              {...disabledProps}
              aria-label={organization.name}
              {...sidebarLinkProps(isSelected)}
              style={isDisabled ? { opacity: 0.4 } : undefined}
            >
              <OrganizationAvatar organizationName={organization.name} size={avatarSize} selected={isSelected} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      );
    });

  const content = (
    <Tooltip.Group>
      {assistantAvailable && (
        <Tooltip label={t('assistant.title')} position={isMobile ? 'bottom' : 'right'} offset={12} withArrow>
          <ActionIcon
            component={Link}
            href={ASSISTANT_PATH}
            aria-label={t('assistant.title')}
            {...sidebarLinkProps(assistantSelected)}
            bd={TRANSPARENT_BORDER}
            style={
              assistantSelected
                ? { boxShadow: '0 0 6px color-mix(in srgb, var(--mantine-color-violet-6) 16%, transparent)' }
                : undefined
            }
          >
            <Avatar src='/icon.png' alt='' size={avatarSize} radius={6} bg='brand.0' p={4} />
            {assistantSelected && <KaliOutline variant='sidebar' />}
          </ActionIcon>
        </Tooltip>
      )}
      {renderOrganizations(myOrganizations)}

      {externalOrganizations.length > 0 && (
        <>
          {isMobile ? (
            // A vertical divider ships with `align-self: stretch`, which pins it to the top of the
            // strip once it is given a height, so it takes the alignment back from the group.
            <Divider
              orientation='vertical'
              h={24}
              mx={4}
              style={{ alignSelf: 'center' }}
              aria-label={t('organizations.external_organizations')}
            />
          ) : (
            <Divider w={32} my={4} aria-label={t('organizations.external_organizations')} />
          )}
          {renderOrganizations(externalOrganizations, true)}
        </>
      )}
    </Tooltip.Group>
  );

  // On a phone the column would eat a sixth of the width, so the organizations line up in a strip
  // under the top navigation instead, scrolling sideways when they do not all fit.
  if (isMobile) {
    return (
      <Group
        component='nav'
        data-organizations-sidebar
        aria-label={t('menu.organizations')}
        align='center'
        gap={6}
        px='sm'
        py={8}
        wrap='nowrap'
        style={{
          flexShrink: 0,
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          background: 'var(--mantine-color-body)',
          overflowX: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        {content}
      </Group>
    );
  }

  return (
    <Stack
      component='nav'
      data-organizations-sidebar
      aria-label={t('menu.organizations')}
      align='center'
      gap={6}
      px={8}
      py='sm'
      w={64}
      style={{
        flexShrink: 0,
        borderRight: '1px solid var(--mantine-color-gray-3)',
        background: 'var(--mantine-color-body)',
        overflowY: 'auto',
        scrollbarWidth: 'none',
      }}
    >
      {content}
    </Stack>
  );
}
